import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuoteModal } from '../../../context/QuoteModalContext';

const OUR_PRODUCTS = [
  { label: 'Standard Stretch Tents', to: '/products/standard-stretch-tents' },
  { label: 'Custom Stretch Tents',   to: '/products/custom-stretch-tents' },
  { label: 'Custom Printed Skins',   to: '/products/custom-printed-skins' },
  { label: 'Bespoke Decor',          to: '/products/bespoke-decor' },
];

const LEARN_MORE = [
  { label: 'Fabric and Colors',   to: '/products/fabric-and-colours' },
  { label: 'Engineering & Safety', to: '/engineering-safety' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { openModal } = useQuoteModal();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleProductsEnter = () => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setIsDropdownOpen(true);
  };

  const handleProductsLeave = () => {
    dropdownTimeout.current = setTimeout(() => setIsDropdownOpen(false), 120);
  };

  const navLinkClass = `text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
    isScrolled ? 'text-gray-800 hover:text-teal-500' : 'text-white hover:text-white/70'
  }`;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/30 backdrop-blur-md border-b border-white/20' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">

          {/* Logo (left zone) */}
          <div className="flex items-center lg:flex-1">
            <Link to="/" aria-label="Pop Pro Tents — home" className="w-32 h-20 flex items-center justify-center">
              <img
                src="/assets/img/ed091f26a7fa.webp"
                alt="Pop Pro Tents"
                className="w-32 h-20 object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* Products with dropdown */}
            <div
              className="relative"
              onMouseEnter={handleProductsEnter}
              onMouseLeave={handleProductsLeave}
            >
              <Link
                to="/products"
                className={`flex items-center gap-1 ${navLinkClass}`}
              >
                PRODUCTS
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-base leading-none"></i>
                </span>
              </Link>

              {/* Dropdown panel */}
              <div
                className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-200 origin-top ${
                  isDropdownOpen
                    ? 'opacity-100 scale-y-100 pointer-events-auto'
                    : 'opacity-0 scale-y-95 pointer-events-none'
                }`}
              >
                {/* Group: Our Products */}
                <div className="px-3 pt-3 pb-1">
                  <p className="text-[10px] font-semibold tracking-widest text-gray-400 uppercase px-2 pb-1">
                    Our Products
                  </p>
                  {OUR_PRODUCTS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line text-teal-400"></i>
                      </span>
                      {item.label}
                    </Link>
                  ))}
                </div>

                {/* Divider */}
                <div className="mx-3 my-1 border-t border-gray-100" />

                {/* Group: Learn More */}
                <div className="px-3 pb-3 pt-1">
                  <p className="text-[10px] font-semibold tracking-widest text-gray-400 uppercase px-2 pb-1">
                    Learn More
                  </p>
                  {LEARN_MORE.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-gray-700 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line text-teal-400"></i>
                      </span>
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link to="/gallery" className={navLinkClass}>CASE STUDIES</Link>
            <Link to="/blog" className={navLinkClass}>BLOG</Link>
            <Link to="/about" className={navLinkClass}>ABOUT</Link>
            <Link to="/contact" className={navLinkClass}>CONTACT</Link>
          </div>

          {/* Right zone: quote CTA (always visible, all sizes) + menu button (mobile) */}
          <div className="flex items-center justify-end gap-2 lg:flex-1">
            {/* Desktop quote */}
            <button
              type="button"
              onClick={openModal}
              className="hidden lg:block bg-teal-400 hover:bg-teal-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              INQUIRE NOW
            </button>

            {/* Mobile quote — compact, keeps the quote constant below lg */}
            <button
              type="button"
              onClick={openModal}
              className="lg:hidden bg-teal-400 hover:bg-teal-500 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              Inquire Now
            </button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg ${isScrolled ? 'text-gray-800' : 'text-white'}`}
            >
              <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t">
          <div className="px-4 py-4 space-y-1">

            {/* Mobile Products accordion */}
            <div>
              <div className="flex items-center justify-between py-2">
                <Link
                  to="/products"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-700 hover:text-teal-500 font-medium text-sm"
                >
                  PRODUCTS
                </Link>
                <button
                  type="button"
                  onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                  className="w-6 h-6 flex items-center justify-center text-gray-500 cursor-pointer"
                >
                  <i className={`ri-arrow-${isMobileProductsOpen ? 'up' : 'down'}-s-line text-lg`}></i>
                </button>
              </div>

              {isMobileProductsOpen && (
                <div className="ml-3 mt-1 mb-2 border-l-2 border-teal-100 pl-3 space-y-0.5">
                  <p className="text-[10px] font-semibold tracking-widest text-gray-400 uppercase pt-1 pb-0.5">
                    Our Products
                  </p>
                  {OUR_PRODUCTS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => { setIsMobileMenuOpen(false); setIsMobileProductsOpen(false); }}
                      className="block text-sm text-gray-600 hover:text-teal-600 py-1.5"
                    >
                      {item.label}
                    </Link>
                  ))}
                  <div className="border-t border-gray-100 my-1" />
                  <p className="text-[10px] font-semibold tracking-widest text-gray-400 uppercase pt-1 pb-0.5">
                    Learn More
                  </p>
                  {LEARN_MORE.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => { setIsMobileMenuOpen(false); setIsMobileProductsOpen(false); }}
                      className="block text-sm text-gray-600 hover:text-teal-600 py-1.5"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/gallery"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-gray-700 hover:text-teal-500 font-medium text-sm py-2"
            >
              CASE STUDIES
            </Link>
            <Link
              to="/blog"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-gray-700 hover:text-teal-500 font-medium text-sm py-2"
            >
              BLOG
            </Link>
            <Link
              to="/about"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-gray-700 hover:text-teal-500 font-medium text-sm py-2"
            >
              ABOUT
            </Link>
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block text-gray-700 hover:text-teal-500 font-medium text-sm py-2"
            >
              CONTACT
            </Link>

            <div className="pt-2">
              <button
                type="button"
                onClick={openModal}
                className="block w-full text-center bg-teal-400 hover:bg-teal-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                INQUIRE NOW
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
